import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Check, X, Zap, ExternalLink } from "lucide-react";
import AmazonButton from "./AmazonButton";
import { optimizeBase44Image, productCardSrcSet } from "@/lib/imageOptimization";

export default function ProductCard({ product }) {
  const hasDirectLink = !!product.amazon_url;

  return (
    <div className="group relative bg-card border border-border rounded-2xl overflow-hidden hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300"
      style={{ '--tw-shadow-color': 'rgba(250,185,0,0.08)' }}>
      {/* Top gradient accent */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-accent/0 via-accent to-accent/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Image + Badge */}
      <div className="relative">
        <div className="aspect-[16/9] bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center overflow-hidden">
          {product.image_url ? (
            <img
              src={optimizeBase44Image(product.image_url, 640, 360)}
              srcSet={productCardSrcSet(product.image_url)}
              sizes="(min-width: 1024px) 380px, (min-width: 768px) 50vw, 100vw"
              width="640"
              height="360"
              alt={`${product.product_name} — ${product.best_for_category} 2026`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-2">
                <Zap className="w-8 h-8 text-accent" />
              </div>
              <p className="text-sm font-mono font-bold text-muted-foreground">{product.wattage || "—"}W</p>
            </div>
          )}
        </div>
        <Badge className="absolute top-3 left-3 bg-accent text-slate-950 font-mono text-[11px] px-2.5 py-1 border-0 font-semibold shadow-md">
          {product.best_for_category}
        </Badge>
        {product.is_bifacial && (
          <Badge className="absolute top-3 right-3 bg-blue-600 text-white font-mono text-[10px] px-2 py-0.5 border-0">
            Bifacial
          </Badge>
        )}
      </div>

      {/* Specs Bar */}
      <div className="grid grid-cols-4 divide-x divide-border border-t border-b border-border bg-secondary/30">
        {[
          { label: "WATTS", value: product.wattage ? `${product.wattage}W` : "—" },
          { label: "EFF.", value: product.claimed_efficiency || "—" },
          { label: "TYPE", value: product.form_factor || "—" },
          { label: "CELL", value: product.panel_type || "—" },
        ].map((spec) => (
          <div key={spec.label} className="py-2.5 px-1 text-center">
            <p className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">{spec.label}</p>
            <p className="text-xs font-heading font-bold mt-0.5 truncate">{spec.value}</p>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-heading font-bold text-lg mb-0.5 leading-snug group-hover:text-accent transition-colors">
          {product.product_name}
        </h3>
        {product.brand && (
          <p className="text-xs text-muted-foreground mb-3 font-mono">by {product.brand}</p>
        )}

        {product.efficiency_notes && (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2 leading-relaxed">
            {product.efficiency_notes}
          </p>
        )}

        {/* Pros */}
        {product.pros?.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] font-mono text-muted-foreground mb-2 uppercase tracking-widest">Who this is for</p>
            <ul className="space-y-1.5">
              {product.pros.slice(0, 3).map((pro, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                  <span className="text-foreground/80">{pro}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Avoid if */}
        {product.avoid_if && (
          <div className="mb-4">
            <p className="text-[10px] font-mono text-muted-foreground mb-2 uppercase tracking-widest">Avoid if</p>
            <p className="flex items-start gap-2 text-sm">
              <X className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
              <span className="text-muted-foreground">{product.avoid_if}</span>
            </p>
          </div>
        )}

        {/* CTAs */}
        <div className="flex flex-col gap-2 mt-4">
          <AmazonButton
            url={product.amazon_url}
            productName={product.product_name}
            category={product.best_for_category}
          />
          {product.review_slug && (
            <Link
              to={`/review/${product.review_slug}`}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 border border-border text-sm font-medium rounded-xl hover:bg-secondary transition-colors"
            >
              Read Full Review <ExternalLink className="w-3.5 h-3.5 opacity-50" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}